import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    const result = await register(name, email, password);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center py-12 px-6"
      style={{
        backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/273d484d-85a8-4eaa-9e45-65aa50be200c/images/dbbd793f1124d050f1df1002a34be6839920e2b2c57eb2c411c5c4eef00d1c7d.png)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="absolute inset-0 bg-[#FDFBF7]/70"></div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-[#5C6056] hover:text-[#2B2E26] transition-colors mb-8 font-body"
          data-testid="back-to-home"
        >
          <ArrowLeft size={18} />
          Back to Home
        </Link>

        <div className="bg-white/90 backdrop-blur-sm p-8 md:p-10 rounded-2xl border border-[#E6E2D6] shadow-lg">
          <div className="text-center mb-8">
            <h1 className="font-heading text-3xl font-medium text-[#2B2E26] mb-2" data-testid="register-title">
              Become a Prayer Warrior
            </h1>
            <p className="font-body text-[#5C6056]">
              Join our community and lift others up in prayer
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm font-body" data-testid="register-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="register-form">
            <div>
              <label className="block text-sm font-body text-[#5C6056] mb-2">Your Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="organic-input"
                placeholder="John Smith"
                required
                data-testid="name-input"
              />
            </div>

            <div>
              <label className="block text-sm font-body text-[#5C6056] mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="organic-input"
                placeholder="your@email.com"
                required
                data-testid="email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-body text-[#5C6056] mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="organic-input pr-10"
                  placeholder="••••••••"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-[#8A8D86] hover:text-[#2B2E26]"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <p className="text-xs text-[#8A8D86] mt-2 font-body">Must be at least 6 characters</p>
            </div>

            <button
              type="submit"
              className="btn-primary w-full"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>

          <div className="mt-8 text-center">
            <p className="font-body text-[#5C6056]">
              Already have an account?{' '}
              <Link to="/login" className="text-[#5A6E4B] hover:text-[#4C5E3F] font-medium" data-testid="login-link">
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
